/*******************************
 * Store reservations
 *******************************/

import uuidv4 from "uuid/v4";
// imports
import Vue from "vue";
import {
	ERequestType,
	fetchGetHelper,
	fetchPostHelper,
	fetchPutHelper,
} from "../../tools/fetch";
import { sendResaConfirmedMessage } from "../../tools/messageSender";
import { resaTradCurrentState } from "../../tools/resaTools";
import {
	compareTime,
	dateToTimeStamp,
	formatTimeH,
	nextTime,
	parseTime,
} from "../../tools/timeTool";
import * as resaState from "../types/resa-state";

/*************************************
 State
 **/
const state = {
	// all resa display for current service and day
	currentResas: [],
	allResas: {},
	lastUpdate: null,
	currentTimeStamp: "",
	currentService: "",
	timeoutDelay: null,
	// storing selected resa
	selectedResa: null,
	// all timer infos displayed if we are in current service
	resasTimerInfo: [],
	// store unConfirmed resas
	unConfirmedResa: {},
	// current stats
	occupationByServiceAndDay: {},
	// hour selected when taking resa
	selectedHour: "",
	// current resa cancelling
	resaCanceling: {},
	isLoaded: false,
	currentResaLoading: false,
	// mode switch objects
	modSwitchTable: false,
	resaToSwitch1: {},
	resaToSwitch2: {},
	floatingSwitchTables: [],
	// oldest month loaded
	oldestMonthLoaded: 0,
	// listing resas dates
	resaListDateStart: "",
	resaListDateEnd: "",
};

/*************************************
 Getters
 **/
const getters = {
	getCurrentResas: function (state, getters, rootState, rootGetters) {
		let currentCalDate = rootGetters["calendar/getCurrentTimestamp"];
		let currentMS = rootGetters["newServices/getSelectedSubService"];
		if (state.allResas === undefined) return [];
		if (state.allResas[currentCalDate.toString()] === undefined) return [];
		if (state.allResas[currentCalDate.toString()][currentMS] === undefined)
			return [];
		state.currentResas = Object.values(
			state.allResas[currentCalDate.toString()][currentMS],
		);
		return state.currentResas;
	},
	getCurrentResasForDay: function (state, getters, rootState, rootGetters) {
		let currentCalDate = rootGetters["calendar/getCurrentTimestamp"];
		if (state.allResas === undefined) return [];
		if (state.allResas[currentCalDate.toString()] === undefined) return [];
		let resasForDay = [];
		Object.entries(state.allResas[currentCalDate.toString()]).forEach((ms) => {
			resasForDay = resasForDay.concat(Object.values(ms[1]));
		});
		return resasForDay;
	},
	getCurrentResasByDay: (state, getters, rootState, rootGetters) => (date) => {
		let timeStamp = dateToTimeStamp(
			date.day,
			date.month - 1,
			date.year,
		).toString();
		if (state.allResas === undefined) return [];
		if (state.allResas[timeStamp.toString()] === undefined) return [];
		let resasForDay = [];
		Object.entries(state.allResas[timeStamp.toString()]).forEach((ms) => {
			resasForDay = resasForDay.concat(Object.values(ms[1]));
		});
		return resasForDay;
	},
	getAllResas: function (state) {
		return state.allResas;
	},
	getLastUpdate: function (state) {
		return state.lastUpdate;
	},
	getCurrentResasByState: (state, getters) => (rState) => {
		return getters["getCurrentResas"].filter(
			(r) => resaTradCurrentState(r.state, r.tids) === rState,
		);
	},
	getResaCanceling: function (state) {
		return state.resaCanceling;
	},
	getCurrentResasLoading: function (state) {
		return state.currentResaLoading;
	},
	getUnConfirmedResa: function (state) {
		let resas = [];
		Object.entries(state.unConfirmedResa).forEach(([ts, tsData]) => {
			Object.entries(tsData).forEach(([service, serviceData]) => {
				Object.entries(serviceData).forEach(([resa, resaData]) => {
					resaData["ts"] = ts;
					resaData["service"] = service;
					resas.push(resaData);
				});
			});
		});

		let ordered = resas.sort((a, b) => {
			if (a.dateResa.date && b.dateResa.date) {
				if (
					new Date(a.dateResa.date).getTime() >
					new Date(b.dateResa.date).getTime()
				)
					return 1;
				if (
					new Date(a.dateResa.date).getTime() <
					new Date(b.dateResa.date).getTime()
				)
					return -1;
				return compareTime(a.hour, b.hour);
			}
		});
		return ordered;
	},
	getSelectedResa: function (state) {
		return state.selectedResa;
	},
	getSelectedResaState: function (state) {
		if (state.selectedResa) {
			switch (state.selectedResa.state) {
				case resaState.CONFIRMED:
					if (
						state.selectedResa.tids !== undefined &&
						state.selectedResa.tids.length > 0
					)
						return resaState.CONFIRMED;
					else return resaState.NEED_TABLE;
				default:
					return state.selectedResa.state;
			}
		} else {
			console.error(
				"selectedResa undefined",
				this.currentTimeStamp,
				this.currentService,
			);
			return resaState.NEED_TABLE;
		}
	},
	getOccupationByServiceAndDayRoom: (state) => (date, ms, room) => {
		let timeStamp = dateToTimeStamp(
			date.day,
			date.month - 1,
			date.year,
		).toString();
		if (
			state.occupationByServiceAndDay[timeStamp] !== undefined &&
			state.occupationByServiceAndDay[timeStamp][ms] !== undefined &&
			state.occupationByServiceAndDay[timeStamp][ms][room] !== undefined
		) {
			return state.occupationByServiceAndDay[timeStamp][ms][room].nbCouv;
		} else {
			return 0;
		}
	},
	getOccupationByServiceAndDay: (state) => (date, ms) => {
		let timeStamp = dateToTimeStamp(
			date.day,
			date.month - 1,
			date.year,
		).toString();
		if (
			state.occupationByServiceAndDay[timeStamp] !== undefined &&
			state.occupationByServiceAndDay[timeStamp][ms] !== undefined
		) {
			let nb = 0;
			Object.entries(state.occupationByServiceAndDay[timeStamp][ms]).forEach(
				([room, rData]) => {
					if (
						rData.state !== resaState.NO_SHOW &&
						rData.state !== resaState.CANCELLED
					)
						nb += parseInt(rData.nbCouv);
				},
			);
			return nb;
		} else {
			return 0;
		}
	},
	getCountNewStateByDay: (state, getters) => (date) => {
		let resas = getters["getCurrentResasByDay"](date);
		let count = 0;
		if (resas.length > 0) {
			resas.forEach((resa) => {
				if (resa.newState) count++;
			});
		}
		return count;
	},
	getCouvByHour: (state, getters) => (hour) => {
		let countCouv = 0;
		if (getters["getCurrentResasForDay"].length > 0) {
			getters["getCurrentResasForDay"].forEach((resaData) => {
				if (hour === resaData.hour) countCouv += resaData.nbCouvert;
			});
		}
		return countCouv;
	},
	getCurrentResaById: (state, getters) => (id) => {
		return getters["getCurrentResas"].find(function (obj) {
			return obj.id === id;
		});
	},
	getCurrentResaDataTimerById: (state) => (id) => {
		return state.resasTimerInfo.find(function (obj) {
			return obj.id === id;
		});
	},
	getSelectedHour: (state) => {
		return state.selectedHour;
	},
	getResaListDateStart: (state) => {
		return state.resaListDateStart;
	},
	getResaListDateEnd: (state) => {
		return state.resaListDateEnd;
	},
	getHourAvaibleForResas:
		(state, getters, rootState, rootGetters) =>
		(resasIds, byPassCurrentTime = false) => {
			let hoursDispo = [];
			let existingResas = [];
			let service = rootGetters["newServices/getSelectedMainServiceData"];
			if (service !== undefined) {
				let today = new Date();
				let day = rootGetters["calendar/getSelectedDay"];
				let currentTime = `${today.getHours()}:${today.getMinutes()}`;
				let hStart = service.hMinTakeResa;
				let hEnd = service.hMaxTakeResa;
				let lenghtService = service.lenghtService;
				let interval = service.intervalResa;

				// check if current time is between hstart and hend
				let current = hStart;
				if (!byPassCurrentTime) {
					while (
						day.isToday &&
						rootGetters["newServices/inCurrentService"] &&
						compareTime(current, currentTime) <= 0 &&
						compareTime(current, hEnd) <= 0
					) {
						current = nextTime(current, interval);
					}
				}

				// get existing resa on table
				if (resasIds.length > 0) {
					resasIds.forEach((resasId) => {
						let resa = getters["getCurrentResaById"](resasId);
						if (resa !== undefined && resa.state !== resaState.DONE) {
							existingResas.push({
								hstart: resa.hour,
								hend: nextTime(resa.hour, lenghtService),
							});
						}
					});
					existingResas.sort(function (a, b) {
						return compareTime(a.hstart, b.hstart);
					});
				}

				// get all interval hours
				let tmpHourDispo = [];
				while (compareTime(current, hEnd) <= 0) {
					tmpHourDispo.push(current);
					current = nextTime(current, interval);
				}
				// compare existing hour and all interval
				if (existingResas.length > 0) {
					tmpHourDispo.forEach((hour) => {
						let found = false;
						let hourEND = nextTime(hour, lenghtService);
						existingResas.forEach((eR) => {
							if (
								(compareTime(hour, eR.hstart) >= 0 &&
									compareTime(hour, eR.hend) < 0) ||
								(compareTime(hourEND, eR.hstart) > 0 &&
									compareTime(hourEND, eR.hend) <= 0)
							) {
								found = true;
							}
						});
						if (!found) hoursDispo.push(hour);
					});
				} else {
					hoursDispo = tmpHourDispo;
				}
			}
			return hoursDispo;
		},
	/** Switch mod getters **/
	getModSwitchTable: function (state) {
		return state.modSwitchTable;
	},
	getResaToSwitch1: function (state) {
		return state.resaToSwitch1;
	},
	getResaToSwitch2: function (state) {
		return state.resaToSwitch2;
	},
	getFloatingSwitchTables: function (state) {
		return state.floatingSwitchTables;
	},
	getAllResasIsLoaded: function (state) {
		return state.isLoaded;
	},
	/**
	 * get link for modify resa
	 * @param {*} param0
	 * @param {*} payload.resa
	 */
	getLinkModify: (state, getters, rootState, rootGetters) => (resa) => {
		let host = `http://${window.location.hostname}`;
		let ts = dateToTimeStamp(
			resa.dateResa.day,
			resa.dateResa.month - 1,
			resa.dateResa.year,
		);
		return (
			`${host}/change/${rootGetters["newUsers/getUidPublic"].toString()}/${ts.toString()}/${resa.id.toString()}`
		);
	},
};

/*************************************
 Mutations
 **/
const mutations = {
	setAllResas(state, dbData) {
		Vue.set(state.allResas, dbData.id.toString(), dbData.data);
	},
	setAddResa(state, payload) {
		if (state.allResas[payload.ts.toString()] === undefined)
			Vue.set(state.allResas, payload.ts.toString(), {});
		if (state.allResas[payload.ts.toString()][payload.service] === undefined)
			Vue.set(state.allResas[payload.ts.toString()], payload.service, {});
		Vue.set(
			state.allResas[payload.ts.toString()][payload.service],
			payload.resaId,
			payload.resaData,
		);
	},
	setRemoveResa(state, payload) {
		if (
			state.allResas[payload.ts.toString()] !== undefined &&
			state.allResas[payload.ts.toString()][payload.service] !== undefined &&
			state.allResas[payload.ts.toString()][payload.service][payload.resaId] !==
				undefined
		) {
			Vue.delete(
				state.allResas[payload.ts.toString()][payload.service],
				payload.resaId,
			);
		}
	},
	setCleanResaForTs(state, payload) {
		if (state.allResas[payload.ts.toString()] !== undefined) {
			Vue.delete(state.allResas, payload.ts.toString());
		}
	},
	setLastUpdate(state, dbData) {
		state.lastUpdate = dbData;
	},
	setCurrentTimeStamp(state, timeStamp) {
		state.currentTimeStamp = timeStamp;
	},
	setOldestMonthTimeStamp(state, timeStamp) {
		state.oldestMonthLoaded = timeStamp;
	},
	setCurrentService(state, service) {
		state.currentService = service;
	},
	setCurrentResasLoading(state, bool) {
		state.currentResaLoading = bool;
	},
	setSelectedResa(state, sResa) {
		state.selectedResa = sResa;
	},
	setResaCanceling(state, resa) {
		state.resaCanceling = resa;
	},
	setResaListDateStart(state, date) {
		state.resaListDateStart = date;
	},
	setResaListDateEnd(state, date) {
		state.resaListDateEnd = date;
	},
	setUnConfirmedResa(state, dbData) {
		Vue.set(state.unConfirmedResa, dbData.id.toString(), dbData.data);
	},
	setOccupationByServiceAndDay(state, dbData) {
		if (!state.occupationByServiceAndDay[dbData.id.toString()]) {
			Vue.set(
				state.occupationByServiceAndDay,
				dbData.id.toString(),
				dbData.data,
			);
		} else {
			if (
				state.occupationByServiceAndDay &&
				state.occupationByServiceAndDay[dbData.id.toString()]
			)
				Vue.delete(state.occupationByServiceAndDay, dbData.id.toString());
			Vue.set(state.occupationByServiceAndDay, dbData.id.toString(), {});
			Object.entries(dbData.data).forEach(([ms, dataService]) => {
				Vue.delete(state.occupationByServiceAndDay[dbData.id.toString()], ms);
				if (!state.occupationByServiceAndDay[dbData.id.toString()][ms]) {
					Vue.set(
						state.occupationByServiceAndDay[dbData.id.toString()],
						ms,
						dataService,
					);
				} else {
					Object.entries(dataService).forEach(([room, dataRoom]) => {
						// remove history occupation
						if (
							room !== "all" &&
							state.occupationByServiceAndDay[dbData.id.toString()][ms]["all"]
						) {
							Vue.delete(
								state.occupationByServiceAndDay[dbData.id.toString()][ms],
								"all",
							);
						}
						// fill occupation tab
						if (
							!state.occupationByServiceAndDay[dbData.id.toString()][ms][room]
						) {
							Vue.set(
								state.occupationByServiceAndDay[dbData.id.toString()][ms],
								room,
								dataRoom,
							);
						} else {
							Vue.set(
								state.occupationByServiceAndDay[dbData.id.toString()][ms][room],
								"nbCouv",
								dataRoom["nbCouv"],
							);
						}
					});
				}
			});
		}
	},
	setResasTimerInfo(state, resaDataT) {
		let index = -1;
		index = state.resasTimerInfo.findIndex((obj) => {
			return obj.id === resaDataT.id;
		});
		if (index !== -1) {
			Vue.set(state.resasTimerInfo, index, resaDataT);
		} else {
			state.resasTimerInfo.push(resaDataT);
		}
	},
	setSelectedHour(state, hour) {
		state.selectedHour = hour;
	},
	resetStore(state) {
		state.currentResas = [];
		state.allResas = {};
		state.resasTimerInfo = [];
		state.unConfirmedResa = {};
		state.unsubscribeResaSnapShot = null;
		state.unsubscribeCurrentResaSnapShot = null;
		state.selectedResa = null;
		state.occupationByServiceAndDay = {};
		state.selectedHour = "";
		state.modSwitchTable = false;
		state.resaToSwitch1 = {};
		state.resaToSwitch2 = {};
		state.floatingSwitchTables = [];
	},
	/** Switch mod setters **/
	setResaToSwitch1(state, dbData) {
		state.resaToSwitch1 = dbData;
	},
	setResaToSwitch2(state, dbData) {
		state.resaToSwitch2 = dbData;
	},
	setFloatingSwitchTables(state, dbData) {
		state.floatingSwitchTables = dbData;
	},
	setModSwitchTable(state, bool) {
		state.modSwitchTable = bool;
	},
	setLoadedState(state, val) {
		state.isLoaded = val;
	},
};

/*************************************
 Action
 **/
const actions = {
	cleanStore: function ({ state, rootState, commit, dispatch }) {
		commit("resetStore");
	},

	/**
	 * createNewResa
	 * @param {*} param0
	 * @param {*} payload
	 */
	async createNewResa(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			// create date key
			let dateKey = dateToTimeStamp(
				payload.date.day,
				payload.date.month - 1,
				payload.date.year,
			);
			let userDatas = rootGetters["newUsers/getUser"];
			let uid = rootGetters["newUsers/getUidPublic"];

			let comment = payload.data.commentResa;
			if (comment === undefined) comment = "";
			let prefix = payload.data.preFix;
			if (prefix === undefined) prefix = "";
			let prefixServ = payload.data.preFixServ;
			if (prefixServ === undefined) prefixServ = "";
			let allergen = payload.data.allergen;
			if (allergen === undefined) allergen = false;
			let allergenMessage = payload.data.allergenMessage;
			if (allergenMessage === undefined) allergenMessage = "";
			let room = payload.data.room;
			let tables = payload.data.tids;
			if (!tables || tables.length === 0) room = "";
			// format data
			let resaData = {
				id: uuidv4(),
				ts: dateKey.toString(),
				restoid: uid,
				cid: payload.cid,
				nbCouvert: payload.data.nbCouvert,
				hour: payload.data.date2.hour,
				discount: payload.discount,
				dateResa: payload.date,
				dateBooking: new Date(),
				room: room,
				tids: tables,
				source: payload.source,
				type: payload.type,
				ms: payload.data.date2.ms,
				subs: payload.data.date2.subs,
				commentResa: comment,
				state: payload.state,
				preFix: prefix,
				preFixServ: prefixServ,
				allergen: allergen,
				allergenMessage: allergenMessage,
				modifyBy: payload.source.toUpperCase(),
			};
			// update rgpd data for client
			commit("setAddResa", {
				ts: dateKey.toString(),
				service: payload.data.date2.subs,
				resaId: resaData.id,
				resaData: resaData,
			});
			// create resa data for resa id -> send to db
			await fetchPostHelper(
				ERequestType.RESAS.CREATE,
				resaData,
				userDatas.token,
			)
				.then((res) => {
					dispatch("updateTableDispo");
					resolve(resaData);
				})
				.catch((err) => {
					console.error(err);
					reject(err);
				});
		});
	},

	/**
	 * feed resa table
	 * @param {*} param0
	 * @param {*} id
	 */
	async feedResas(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		datas,
	) {
		return new Promise(async (resolve, reject) => {
			if (datas.load) commit("setCurrentResasLoading", true);
			let resas = datas.resas;
			// check resas length before do something
			if (resas.length > 0) {
				let todayTS = rootGetters["calendar/getTodayTimestamp"];
				let currentTS = rootGetters["calendar/getCurrentTimestamp"];
				// inits tabs
				let resasTab = {};
				let occupation = {};
				let unconfirmed = {};

				// format datas
				for (let i = 0; i < Object.entries(resas).length; i++) {
					let resa = resas[i];
					// push resa data to the list
					let rData = await dispatch("checkTableAttrib", resa);
					// check if client exist garde fou
					let searchClient = rootGetters["newClients/getClientByid"](rData.cid);
					if (!searchClient) {
						// refresh client database
						let client = await dispatch(
							"newClients/refreshClientDatasByCid",
							{
								restoid: rootGetters["newUsers/getUidPublic"],
								cid: rData.cid,
							},
							{ root: true },
						);
						if (!client) {
							console.error("Client not FOUND when fetching resa");
							return;
						}
					}

					// all resas
					if (resasTab[rData.ts.toString()] === undefined)
						Vue.set(resasTab, rData.ts.toString(), {});
					if (resasTab[rData.ts.toString()][rData.ms] === undefined)
						Vue.set(resasTab[rData.ts.toString()], rData.ms, {});
					Vue.set(resasTab[rData.ts.toString()][rData.ms], rData.id, rData);

					// occupation
					if (
						rData.state !== resaState.CANCELLED &&
						rData.state !== resaState.TO_CONFIRM &&
						rData.state !== resaState.NO_SHOW
					) {
						if (occupation[rData.ts.toString()] === undefined)
							Vue.set(occupation, rData.ts.toString(), {});
						if (occupation[rData.ts.toString()][rData.ms] === undefined)
							Vue.set(occupation[rData.ts.toString()], rData.ms, {});
						if (
							occupation[rData.ts.toString()][rData.ms][rData.room] ===
							undefined
						)
							Vue.set(occupation[rData.ts.toString()][rData.ms], rData.room, {
								nbCouv: 0,
							});
						occupation[rData.ts.toString()][rData.ms][rData.room].nbCouv +=
							parseInt(rData.nbCouvert);
					}

					// unconfirmed
					if (
						parseInt(rData.ts, 10) >= todayTS &&
						rData.state === resaState.TO_CONFIRM
					) {
						if (unconfirmed[rData.ts.toString()] === undefined)
							Vue.set(unconfirmed, rData.ts.toString(), {});
						if (unconfirmed[rData.ts.toString()][rData.ms] === undefined)
							Vue.set(unconfirmed[rData.ts.toString()], rData.ms, {});
						Vue.set(
							unconfirmed[rData.ts.toString()][rData.ms],
							rData.id,
							rData,
						);
					}

					// check and update unconfirmedres
					if (rData.state !== resaState.TO_CONFIRM) {
						dispatch("updateUnconfirmedResa", {
							state: rData.state,
							data: rData,
							service: rData.ms,
							ts: rData.ts.toString(),
						});
					}
				}

				// update store allResas
				Object.entries(resasTab).forEach(([ts, resaForServices]) => {
					commit("setAllResas", { id: ts, data: resaForServices });
				});

				// update store occupation
				if (Object.entries(occupation).length > 0) {
					Object.entries(occupation).forEach(([ts, occupationCount]) => {
						commit("setOccupationByServiceAndDay", {
							id: ts,
							data: occupationCount,
						});
					});
				} else {
					commit("setOccupationByServiceAndDay", { id: currentTS, data: {} });
				}

				// update store unconfirmed
				if (Object.keys(unconfirmed).length > 0) {
					Object.entries(unconfirmed).forEach(([ts, unconfirmDatas]) => {
						commit("setUnConfirmedResa", {
							id: ts.toString(),
							data: unconfirmDatas,
						});
					});
				}
			}

			// update Table dispo
			dispatch("updateTableDispo");
			// update last update info
			commit("setLastUpdate", new Date());
			// stop loading
			commit("setCurrentResasLoading", false);
			// update local client datas
			for (let i = 0; i < Object.entries(resas).length; i++) {
				let resa = resas[i];
				// push resa data to the list
				let rData = await dispatch("checkTableAttrib", resa);

				// refresh client database
				// check if client exist garde fou
				let searchClient = rootGetters["newClients/getClientByid"](rData.cid);
				if (!searchClient) {
					let client = await dispatch(
						"newClients/refreshClientDatasByCid",
						{
							restoid: rootGetters["newUsers/getUidPublic"],
							cid: rData.cid,
						},
						{ root: true },
					);
					if (!client) {
						console.error("Client not FOUND when fetching resa");
						return;
					}
				}
			}
			resolve();
		});
	},

	/**
	 * Sync all resas and store it
	 */
	async syncAllResas(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		load = true,
	) {
		return new Promise(async (resolve, reject) => {
			let selectedDay = rootGetters["calendar/getSelectedDay"];
			let firstDayOfMonthTs = dateToTimeStamp(
				1,
				selectedDay.month - 1,
				selectedDay.year,
			);

			let uid = rootGetters["newUsers/getUidPublic"];
			let userDatas = rootGetters["newUsers/getUser"];
			// get data to first day of month and futures current timestamp
			await fetchGetHelper(
				ERequestType.RESAS.GET_CURRENT_FUTURE,
				`restoid=${uid}&ts=${firstDayOfMonthTs}`,
				userDatas.token,
			)
				.then(async (resas) => {
					await fetchGetHelper(
						ERequestType.RESAS.GET_CLIENT_CURRENT_FUTURE,
						`restoid=${uid}&ts=${firstDayOfMonthTs}`,
						userDatas.token,
					).then((clients) => {
						dispatch("newClients/refreshClientsDatasByCids", clients.data, {
							root: true,
						});
					});
					dispatch("feedResas", { resas: resas.data, load: load });
					if (load) {
						commit("setLoadedState", true);
					}
					resolve(resas.data);
				})
				.catch((err) => {
					console.error(err);
					if (load) {
						commit("setLoadedState", true);
					}
					reject(err);
				});
		});
	},

	/**
	 * Sync all resas and store it
	 */
	async loadCidResasByResto(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		cid,
	) {
		return new Promise(async (resolve, reject) => {
			let uid = rootGetters["newUsers/getUidPublic"];
			let userDatas = rootGetters["newUsers/getUser"];
			// get data to first day of month and futures current timestamp
			await fetchGetHelper(
				ERequestType.RESAS.GET_ALL_BY_CID,
				`restoid=${uid}&cid=${cid}`,
				userDatas.token,
			)
				.then((resas) => {
					resolve(resas.data);
				})
				.catch((err) => {
					console.error(err);
					reject(err);
				});
		});
	},

	/**
	 * LoadResa by timestamp
	 * @param {*} param0 store params
	 * @param {*} id timestamp to load
	 */
	async loadByTimestamp(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		id,
	) {
		// init vars
		// let todayTS = rootGetters['calendar/getTodayTimestamp']
		let uid = rootGetters["newUsers/getUidPublic"];
		let userDatas = rootGetters["newUsers/getUser"];

		try {
			// get data for current timestamp
			await fetchGetHelper(
				ERequestType.RESAS.GET_BY_TS,
				`restoid=${uid}&ts=${id}`,
				userDatas.token,
			)
				.then(async (resas) => {
					await fetchGetHelper(
						ERequestType.RESAS.GET_CLIENT_BY_TS,
						`restoid=${uid}&ts=${id}`,
						userDatas.token,
					).then((clients) => {
						dispatch("newClients/refreshClientsDatasByCids", clients.data, {
							root: true,
						});
					});
					dispatch("feedResas", { resas: resas.data, load: true });
				})
				.catch((err) => {
					console.error(err);
					// reject(err)
				});
		} catch (err) {
			console.error("ERROR DATABASE CONNECTION NEED TO RELOAD", err);
			window.localStorage.clear();
			window.location.reload();
		}
	},

	async loadNbCouvByHour(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			try {
				await fetchGetHelper(
					ERequestType.RESAS.GET_TS_COUV_BY_HOUR,
					`restoid=${payload.uid}&ts=${payload.ts}`,
				).then((res) => {
					resolve(res);
				});
			} catch (err) {
				console.error("ERROR on get nb couv by hour", err);
				reject(err);
			}
		});
	},

	/**
	 * Sync only occupation for old month
	 */
	async syncOccupationOnly({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		// selected calendar entries
		let selectedMonth = rootGetters["calendar/getMonth"];
		let selectedYear = rootGetters["calendar/getYear"];

		// today info
		let todayTS = rootGetters["calendar/getTodayTimestamp"];
		let todayDate = new Date(todayTS);
		let todayMonth = todayDate.getMonth() + 1;
		let todayYear = todayDate.getFullYear();

		// check if select month and year is older than today
		if (
			selectedYear < todayYear ||
			(selectedMonth < todayMonth && selectedYear === todayYear)
		) {
			// create tsid tab of start and end
			let startTsId = dateToTimeStamp(1, selectedMonth - 1, selectedYear);
			let lastDayOfMonth = new Date(selectedYear, selectedMonth, 0);
			let endTsId = dateToTimeStamp(
				lastDayOfMonth.getDate(),
				lastDayOfMonth.getMonth(),
				lastDayOfMonth.getFullYear(),
			);

			let uid = rootGetters["newUsers/getUidPublic"];
			let userDatas = rootGetters["newUsers/getUser"];
			await fetchGetHelper(
				ERequestType.RESAS.GET_TS_TO_TS,
				`restoid=${uid}&minTs=${startTsId}&maxTs=${endTsId}`,
				userDatas.token,
			)
				.then(async (resas) => {
					await fetchGetHelper(
						ERequestType.RESAS.GET_CLIENT_TS_TO_TS,
						`restoid=${uid}&minTs=${startTsId}&maxTs=${endTsId}`,
						userDatas.token,
					).then((clients) => {
						dispatch("newClients/refreshClientsDatasByCids", clients.data, {
							root: true,
						});
					});
					dispatch("feedResas", { resas: resas.data, load: true });
					// resolve(res)
				})
				.catch((err) => {
					console.error(err);
					// reject(err)
				});
		}
	},

	/**
	 * Sync only occupation for old month
	 */
	async getResasTsToTs(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// create tsid tab of start and end
		let startTsId = dateToTimeStamp(
			payload.startDay,
			payload.startMonth,
			payload.startYear,
		);
		let endTsId = dateToTimeStamp(
			payload.endDay,
			payload.endMonth,
			payload.endYear,
		);

		let uid = rootGetters["newUsers/getUidPublic"];
		let userDatas = rootGetters["newUsers/getUser"];
		return new Promise(async (resolve, reject) => {
			try {
				await fetchGetHelper(
					ERequestType.RESAS.GET_TS_TO_TS,
					`restoid=${uid}&minTs=${startTsId}&maxTs=${endTsId}`,
					userDatas.token,
				).then((resas) => {
					resolve(resas.data);
				});
			} catch (err) {
				console.error("ERROR on get nb couv by hour", err);
				reject(err);
			}
		});
	},

	/**
	 * Sync resa in realtime for current selected day and service
	 */
	async syncRTCurrentResas({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		let currentCalDate = rootGetters["calendar/getCurrentTimestamp"];
		let uid = rootGetters["newUsers/getUidPublic"];
		let userDatas = rootGetters["newUsers/getUser"];
		return new Promise(async (resolve, reject) => {
			// get data to first day of month and futures current timestamp
			await fetchGetHelper(
				ERequestType.RESAS.GET_BY_TS,
				`restoid=${uid}&ts=${currentCalDate}`,
				userDatas.token,
			)
				.then(async (resas) => {
					// preload clients for current day
					await fetchGetHelper(
						ERequestType.RESAS.GET_CLIENT_BY_TS,
						`restoid=${uid}&ts=${currentCalDate}`,
						userDatas.token,
					).then((clients) => {
						dispatch("newClients/refreshClientsDatasByCids", clients.data, {
							root: true,
						});
					});
					if (resas.data.length === 0) {
						commit("setCleanResaForTs", {
							ts: currentCalDate,
						});
						commit("newRooms/clearAllTableState", null, { root: true });
					} else {
						dispatch("feedResas", { resas: resas.data, load: true }).then(
							() => {
								resolve();
							},
						);
					}
				})
				.catch((err) => {
					console.error(err);
					// reject(err)
				});
		});
	},

	/**
	 * Check table attribution still exist
	 * @param {*} param0 store param
	 * @param {*} resa resa to check
	 */
	checkTableAttrib: function (
		{ state, rootState, commit, dispatch, getters, rootGetters },
		resa,
	) {
		let resaData = resa;
		let room = resa.room;
		let tableIds = resa.tids;
		if (room !== "") {
			// check if table was delete
			// MAYBE: improve it for better perf find an other way to do this
			let removeVal = [];
			tableIds.forEach((tid) => {
				if (rootGetters["newRooms/getTablesByRoom"](room) !== null) {
					let found = Object.keys(
						rootGetters["newRooms/getTablesByRoom"](room),
					).find((id) => {
						return id === tid.toString();
					});
					// table isn't exist add to remove tab
					if (!found) {
						removeVal.push(tid);
					}
				} else {
					// room is undefined deleted room -> unassign
					room = "";
					resaData.room = "";
					resaData.tids = [];
					// not unassign in BDD if the room deletion is an error
				}
			});
			if (removeVal.length > 0) {
				// tid to remove
				removeVal.forEach((tid) => {
					let index = tableIds.indexOf(tid);
					if (index !== -1) {
						tableIds.splice(index, 1);
					}
				});
				resaData.tids = tableIds;
				if (tableIds.length === 0) resaData.room = "";
			}
		}
		return resaData;
	},

	/**
	 * un link table
	 * @param {*} param0 store param
	 * @param {*} payload resa data
	 */
	unLinkTables: function (
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise((resolve, reject) => {
			if (payload.resa.room !== "") {
				payload.resa.tids.forEach((tid) => {
					commit(
						"newRooms/setTableState",
						{
							room: payload.resa.room,
							tid: tid,
							value: false,
							occuped: false,
							resaId: "",
						},
						{ root: true },
					);
				});
			}
			dispatch("updateTablesLinked", {
				state: payload.stateResa,
				data: payload.resa,
				tids: [],
				room: "",
			}).then(() => {
				resolve();
			});
		});
	},

	/**
	 * update table state for current resas
	 * @param {*} param0 store param
	 */
	updateTableDispo: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		let roomDatas = rootGetters["newRooms/getRoomsDatas"];
		if (roomDatas === null) return;
		Object.entries(roomDatas).forEach(([rId, rData]) => {
			let nbCouvertsBooked = 0;
			Object.entries(rData.tables).forEach(([tId, tData]) => {
				let toUpdate = tData;
				// reset table state
				toUpdate.isBooked = false;
				toUpdate.isOccuped = false;
				toUpdate.resaId = [];
				// find current state
				Object.entries(getters["getCurrentResas"]).forEach(
					([resaKey, resaData]) => {
						if (resaData.room !== undefined && resaData.room !== "") {
							resaData.tids.forEach((linkTableId) => {
								if (
									rData.id === resaData.room &&
									tId.toString() === linkTableId.toString() &&
									resaData.state !== "CANCELLED"
								) {
									toUpdate.isBooked = true;
									if (resaData.state === resaState.SHOW)
										toUpdate.isOccuped = true;
									if (
										resaData.state === resaState.DONE ||
										resaData.state === resaState.NO_SHOW
									)
										toUpdate.isBooked = false;
									if (
										resaData.state !== resaState.DONE &&
										resaData.state !== resaState.NO_SHOW
									)
										toUpdate.resaId.push(resaData.id);
									if (resaData.state !== resaState.NO_SHOW)
										nbCouvertsBooked += parseInt(toUpdate.nbCouvert);
								}
							});
						}
					},
				);
				// update table state in room store data
				commit(
					"newRooms/setTableState",
					{
						room: rData.id,
						tid: toUpdate.id,
						value: toUpdate.isBooked,
						resaId: toUpdate.resaId,
						occuped: toUpdate.isOccuped,
					},
					{ root: true },
				);
			});
			commit(
				"newRooms/setRoomNbCouvertsBooked",
				{ id: rData.id, value: nbCouvertsBooked },
				{ root: true },
			);
		});
	},
	updateUnconfirmedResa: function (
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		// remove of unConfirm tab
		if (
			payload.state !== resaState.TO_CONFIRM &&
			state.unConfirmedResa[payload.ts.toString()] !== undefined &&
			state.unConfirmedResa[payload.ts.toString()][payload.service] !==
				undefined &&
			state.unConfirmedResa[payload.ts.toString()][payload.service][
				payload.data.id
			] !== undefined
		) {
			Vue.delete(
				state.unConfirmedResa[payload.ts.toString()][payload.service],
				payload.data.id,
			);
		}
	},
	async moveResaTo(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			// update local modifications
			// set new resa in local resa tab
			commit("setAddResa", {
				ts: payload.newTs,
				service: payload.newService,
				resaId: payload.data.id,
				resaData: payload.data,
			});
			// delete old one
			commit("setRemoveResa", {
				ts: payload.prevTs,
				service: payload.prevService,
				resaId: payload.data.id,
			});
			dispatch("updateUnconfirmedResa", {
				state: payload.state,
				data: payload.data,
				service: payload.prevService,
				ts: payload.prevTs,
			});

			// get auth datas
			let userDatas = rootGetters["newUsers/getUser"];
			let uid = rootGetters["newUsers/getUidPublic"];
			// delete resa
			let resaToDelete = {
				id: payload.data.id,
				restoid: uid,
			};
			// new resa
			let resaToCreate = payload.data;
			resaToCreate.ts = payload.newTs;
			resaToCreate.ms = payload.newService;

			// update database information
			await fetchPostHelper(
				ERequestType.RESAS.DELETE,
				resaToDelete,
				userDatas.token,
			)
				.then(async (updatedR) => {
					await fetchPostHelper(
						ERequestType.RESAS.CREATE,
						resaToCreate,
						userDatas.token,
					)
						.then((res) => {
							dispatch("updateTableDispo");
							resolve(resaToCreate);
						})
						.catch((err) => {
							console.error(err);
							reject(err);
						});
				})
				.catch(function (error) {
					// The document probably doesn't exist.
					console.error(error);
					reject(error);
				});
		});
	},

	/**
	 * Accept resa send mail to client for confirm it and update state resa + update count client stats
	 * @param {*} param0
	 * @param {*} payload.resa
	 */
	async acceptResa(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			let hourFormatted = formatTimeH(parseTime(payload.resa.hour));
			let client = rootGetters["newClients/getClientByid"](payload.resa.cid);
			// send confirmed mail
			sendResaConfirmedMessage({
				clientName: `${client.name} ${client.prenom}`,
				clientEmail: client.email,
				restoName: rootGetters["infos/getAllInfos"].restoName,
				restoEmail: rootGetters["infos/getAllInfos"].restoMail,
				dateResa:
					`${payload.resa.dateResa.labelDay} ${payload.resa.dateResa.day.toString()} ${payload.resa.dateResa.labelMonth} ${payload.resa.dateResa.year.toString()}`,
				hourResa: hourFormatted,
				message: rootGetters["infos/getAllInfos"].resaAcceptedMessage,
				nbCouverts: payload.resa.nbCouvert,
				adresse: rootGetters["infos/getAllInfos"].restoAdresse,
				tel: rootGetters["infos/getAllInfos"].restoTel,
				url: getters["getLinkModify"](payload.resa),
				type: payload.resa.type,
				total: payload.resa.total,
				restoid: rootGetters["infos/getAllInfos"].restoid,
				discount: payload.resa.discount,
				sendSms: !!rootGetters["infos/getAllInfos"].clientSmsSended,
				clientTel: client.tel,
				clientTelPreFix: client.telPreFix,
			});
			// update state resa
			let stateToSet = payload.state;
			if (!stateToSet) stateToSet = resaState.CONFIRMED;
			let selectedTables = rootGetters["newRooms/getSelectedTables"];
			let selectedRoom = rootGetters["newRooms/getSelectedRoom"];
			if (selectedTables.length > 0 && selectedRoom !== "") {
				await dispatch("updateTablesLinked", {
					state: resaState.CONFIRMED,
					data: payload.resa,
					tids: selectedTables,
					room: selectedRoom,
				});
			} else {
				await dispatch("updateStateResa", {
					data: payload.resa,
					state: stateToSet,
					ts: payload.resa.ts,
					service: payload.resa.service,
				});
			}
			// update client count stats
			await dispatch(
				"newClients/updateClientCount",
				{
					cid: payload.resa.cid,
					type: "countResa",
				},
				{ root: true },
			);
			resolve();
		});
	},

	/**
	 * update payement info
	 */
	async updatePayementInfoDone(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			let toUpdate = payload.data;
			toUpdate.payementDone = payload.payementDone;

			// update local data
			commit("setAddResa", {
				ts: payload.ts,
				service: payload.service,
				resaId: toUpdate.id,
				resaData: toUpdate,
			});

			// update database information
			let userDatas = rootGetters["newUsers/getUser"];
			await fetchPutHelper(ERequestType.RESAS.UPDATE, toUpdate, userDatas.token)
				.then((updatedR) => {
					resolve(toUpdate);
				})
				.catch(function (error) {
					// The document probably doesn't exist.
					console.error(error);
					reject(error);
				});
		});
	},

	async updateNewStateResa(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			let toUpdate = Object.assign({}, payload);
			toUpdate.newState = false;

			let userDatas = rootGetters["newUsers/getUser"];
			await fetchPutHelper(
				ERequestType.RESAS.LAZY_UPDATE,
				toUpdate,
				userDatas.token,
			)
				.then((updatedR) => {
					resolve(toUpdate);
					// dispatch('syncRTCurrentResas')
				})
				.catch(function (error) {
					// The document probably doesn't exist.
					console.error(error);
					reject(error);
				});
		});
	},

	/**
	 * update State Resa
	 * @param {*} param0
	 * @param {*} payload
	 */
	async updateStateResa(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			let toUpdate = payload.data;
			// remove of unConfirm tab
			dispatch("updateUnconfirmedResa", payload);
			// force state
			toUpdate.state = payload.state;
			if (
				payload.state === resaState.NO_SHOW ||
				payload.state === resaState.CANCELLED
			)
				toUpdate.room = "";
			// update local data
			commit("setAddResa", {
				ts: payload.ts,
				service: payload.service,
				resaId: toUpdate.id,
				resaData: toUpdate,
			});
			// update database information
			let userDatas = rootGetters["newUsers/getUser"];
			await fetchPutHelper(ERequestType.RESAS.UPDATE, toUpdate, userDatas.token)
				.then((updatedR) => {
					resolve(toUpdate);
					dispatch("syncRTCurrentResas");
				})
				.catch(function (error) {
					// The document probably doesn't exist.
					console.error(error);
					reject(error);
				});
		});
	},

	/**
	 * update Tables Linked
	 * @param {*} param0
	 * @param {*} payload
	 */
	async updateTablesLinked(
		{ state, rootState, commit, dispatch, getters, rootGetters },
		payload,
	) {
		return new Promise(async (resolve, reject) => {
			let currentCalDate = rootGetters["calendar/getCurrentTimestamp"];
			let currentMS = rootGetters["newServices/getSelectedSubService"];
			let toUpdate = Object.assign({}, payload.data);
			// remove of unConfirm tab
			dispatch("updateUnconfirmedResa", {
				state: payload.state,
				data: payload.data,
				service: currentMS,
				ts: currentCalDate,
			});
			// force state
			toUpdate.state = payload.state;
			// force tids
			toUpdate.tids = [];
			// force room
			toUpdate.room = payload.room;
			if (payload.tids !== undefined && payload.tids.length > 0)
				toUpdate.tids = payload.tids;
			else toUpdate.tids.length = 0;
			// update local data
			commit("setAddResa", {
				ts: currentCalDate,
				service: currentMS,
				resaId: toUpdate.id,
				resaData: toUpdate,
			});

			// update database information
			let userDatas = rootGetters["newUsers/getUser"];
			await fetchPutHelper(ERequestType.RESAS.UPDATE, toUpdate, userDatas.token)
				.then((updatedR) => {
					if (
						getters["getSelectedResa"] !== null &&
						getters["getSelectedResa"].id === toUpdate.id
					)
						commit("setSelectedResa", toUpdate);
					resolve(toUpdate);
					dispatch("syncRTCurrentResas");
				})
				.catch(function (error) {
					// The document probably doesn't exist.
					console.error(error);
					reject(error);
				});
		});
	},

	/**
	 * update Switchable Resa By Resa
	 * @param {} param0
	 * @param {*} resa
	 */
	updateSwitchableResaByResa: function (
		{ state, rootState, commit, dispatch, getters, rootGetters },
		resa,
	) {
		if (
			state.resaToSwitch1.data === undefined &&
			(state.resaToSwitch2.data === undefined ||
				state.resaToSwitch2.data.id !== resa.id)
		) {
			commit("setResaToSwitch1", {
				state: resa.state,
				data: resa,
				tids: resa.tids.slice(0),
				room: resa.room,
			});
		} else if (
			state.resaToSwitch2.data === undefined &&
			(state.resaToSwitch1.data === undefined ||
				state.resaToSwitch1.data.id !== resa.id)
		) {
			// check if double resa and tids is equal
			if (resa.tids.some((e) => state.resaToSwitch1.tids.includes(e))) {
				commit("setResaToSwitch1", {
					state: resa.state,
					data: resa,
					tids: resa.tids.slice(0),
					room: resa.room,
				});
			} else {
				commit("setResaToSwitch2", {
					state: resa.state,
					data: resa,
					tids: resa.tids.slice(0),
					room: resa.room,
				});
			}
		} else if (
			state.resaToSwitch1.data !== undefined &&
			state.resaToSwitch2.data === undefined &&
			state.resaToSwitch1.data.id === resa.id
		) {
			commit("setResaToSwitch1", {});
		} else if (
			state.resaToSwitch2.data !== undefined &&
			state.resaToSwitch2.data.id === resa.id
		) {
			commit("setResaToSwitch2", {});
		}
		dispatch("selectTableToSwitch");
	},

	/**
	 * Selecte table to switch
	 * @param {*} param0
	 */
	selectTableToSwitch: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		let selectedTables = rootGetters["newRooms/getSelectedTables"];
		// conserve floating table selected
		let floatingST = [];
		selectedTables.forEach((tid) => {
			if (
				rootGetters["newRooms/getLinkedResaIdsToTable"](
					rootGetters["newRooms/getSelectedRoom"],
					tid,
				).length === 0
			) {
				floatingST.push(tid);
			}
		});
		let selectedTids = [];
		if (
			state.resaToSwitch1.data !== undefined &&
			state.resaToSwitch1.room === rootGetters["newRooms/getSelectedRoom"]
		)
			selectedTids = [].concat.apply(selectedTids, state.resaToSwitch1.tids);
		if (
			state.resaToSwitch2.data !== undefined &&
			state.resaToSwitch2.room === rootGetters["newRooms/getSelectedRoom"]
		)
			selectedTids = [].concat.apply(selectedTids, state.resaToSwitch2.tids);
		if (state.floatingSwitchTables.length > 0)
			selectedTids = [].concat.apply(selectedTids, state.floatingSwitchTables);
		// reinit selected tables
		commit("newRooms/setSelectedTables", selectedTids, { root: true });
	},

	/**
	 * Switch Resas
	 * @param {*} param0
	 */
	switchResa: function ({
		state,
		rootState,
		commit,
		dispatch,
		getters,
		rootGetters,
	}) {
		return new Promise((resolve, reject) => {
			let selectedTables = rootGetters["newRooms/getSelectedTables"];
			if (
				state.resaToSwitch1.data !== undefined &&
				state.resaToSwitch2.data === undefined
			) {
				if (
					state.resaToSwitch1.room !== rootGetters["newRooms/getSelectedRoom"]
				) {
					dispatch("updateTablesLinked", {
						state: state.resaToSwitch1.state,
						data: state.resaToSwitch1.data,
						tids: selectedTables,
						room: rootGetters["newRooms/getSelectedRoom"],
					});
				} else {
					let tableDestination = selectedTables.filter(
						(tid) => !state.resaToSwitch1.tids.includes(tid),
					);
					dispatch("updateTablesLinked", {
						state: state.resaToSwitch1.state,
						data: state.resaToSwitch1.data,
						tids: tableDestination,
						room: state.resaToSwitch1.room,
					});
				}
				resolve("Changement de table réalisé");
			} else if (
				state.resaToSwitch1.data !== undefined &&
				state.resaToSwitch2.data !== undefined
			) {
				// get all resa for selected tables in switch 1
				let allResaOnSwitch1 = [];
				state.resaToSwitch1.tids.forEach((tid) => {
					rootGetters["newRooms/getLinkedResaIdsToTable"](
						state.resaToSwitch1.room,
						tid,
					).forEach((resa) => {
						let found = allResaOnSwitch1.find((element) => {
							return element === resa;
						});
						if (found === undefined) allResaOnSwitch1.push(resa);
					});
				});
				// remove main resa to switch
				allResaOnSwitch1 = allResaOnSwitch1.filter(
					(resa) => state.resaToSwitch1.data.id !== resa,
				);
				// get all hours avaible for switch 1 table selected
				let avaibleHoursSwitch1 = getters["getHourAvaibleForResas"](
					allResaOnSwitch1,
					true,
				);
				// Same thing for switch 2
				let allResaOnSwitch2 = [];
				state.resaToSwitch2.tids.forEach((tid) => {
					rootGetters["newRooms/getLinkedResaIdsToTable"](
						state.resaToSwitch2.room,
						tid,
					).forEach((resa) => {
						let found = allResaOnSwitch2.find((element) => {
							return element === resa;
						});
						if (found === undefined) allResaOnSwitch2.push(resa);
					});
				});
				allResaOnSwitch2 = allResaOnSwitch2.filter(
					(resa) => state.resaToSwitch2.data.id !== resa,
				);
				let avaibleHoursSwitch2 = getters["getHourAvaibleForResas"](
					allResaOnSwitch2,
					true,
				);
				// check avaibilty for destination
				let canSwitch1 = avaibleHoursSwitch2.find((hour) => {
					return hour === state.resaToSwitch1.data.hour;
				});
				let canSwitch2 = avaibleHoursSwitch1.find((hour) => {
					return hour === state.resaToSwitch2.data.hour;
				});
				// if hours isn't avaible for each switch reject an error
				if (canSwitch1 === undefined || canSwitch2 === undefined) {
					reject(new Error("Conflit dans les horaires des tables"));
				} else {
					// if hours is avaible we can switch
					dispatch("updateTablesLinked", {
						state: state.resaToSwitch1.state,
						data: state.resaToSwitch1.data,
						tids: state.resaToSwitch2.tids,
						room: state.resaToSwitch2.room,
					});
					dispatch("updateTablesLinked", {
						state: state.resaToSwitch2.state,
						data: state.resaToSwitch2.data,
						tids: state.resaToSwitch1.tids,
						room: state.resaToSwitch1.room,
					});
					resolve("Changement de table réalisé");
				}
			}
		});
	},
};

/*************************************
 Helpers
 **/

/*************************************
 Export module
 **/
export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
